import {
  DbConnection,
  DbConnectionBuilder,
  reducers,
  type ReducerEventContext
} from '../generated';
import type { SpaceTimeConfig } from '../config';
import type { Logger } from '../types';
import type { ProductType } from 'spacetimedb';

type BuilderFactory = () => DbConnectionBuilder;

export interface ConnectionManagerOptions {
  readonly config: SpaceTimeConfig;
  readonly moduleName?: string;
  readonly builderFactory?: BuilderFactory;
  readonly logger?: Logger;
}

export class ConnectionManager {
  private static readonly SESSION_HEARTBEAT_INTERVAL_MS = 15_000;

  private readonly config: SpaceTimeConfig;
  private readonly defaultModuleName: string;
  private readonly builderFactory: BuilderFactory;
  private readonly logger: Logger;

  private connection: DbConnection | null = null;
  private connectPromise: Promise<DbConnection> | null = null;
  private disconnectExpected = false;
  private disconnectCleanupInFlight = false;
  private sessionHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: ConnectionManagerOptions) {
    this.config = options.config;
    this.defaultModuleName = options.moduleName ?? options.config.schema;
    this.logger = options.logger ?? (() => undefined);
    this.builderFactory = options.builderFactory ?? (() => DbConnection.builder());
  }

  public getConfig(): SpaceTimeConfig {
    return this.config;
  }

  public getPlayerId(): string | null {
    return this.config.playerId ?? null;
  }

  private resolveModuleName(): string {
    return this.config.schema || this.defaultModuleName;
  }

  public async connect(): Promise<void> {
    await this.ensureConnection();
  }

  public async disconnect(options?: { skipLogout?: boolean }): Promise<void> {
    this.connectPromise = null;
    this.stopSessionHeartbeat();

    if (!options?.skipLogout) {
      try {
        await this.sendLogoutSignal();
      } catch (error) {
        this.logger('logout_before_disconnect_failed', {
          message: (error as Error).message
        });
      }
    }

    if (this.connection?.isActive) {
      this.disconnectExpected = true;
      try {
        this.connection.disconnect();
      } finally {
        this.connection = null;
        this.disconnectExpected = false;
      }
    } else {
      this.connection = null;
    }
  }

  public isConnected(): boolean {
    return Boolean(this.connection?.isActive);
  }

  public async ensureConnection(): Promise<DbConnection> {
    if (this.connection?.isActive) {
      return this.connection;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise<DbConnection>((resolve, reject) => {
      const builder = this.builderFactory();
      let settled = false;

      builder
        .withUri(this.config.endpoint)
        .withDatabaseName(this.resolveModuleName());

      if (this.config.walletPrivateKey) {
        builder.withToken(this.config.walletPrivateKey);
      }

      builder
        .onConnect((connection) => {
          this.installDbAccessorAliases(connection);
          this.subscribeToCoreTables(connection)
            .then(() => {
              settled = true;
              this.connection = connection;
              resolve(connection);
            })
            .catch((error) => {
              if (!settled) {
                settled = true;
                reject(error);
              }
            });
        })
        .onConnectError((_ctx, error) => {
          if (!settled) {
            settled = true;
            reject(error);
          }
        })
        .onDisconnect((_ctx, error) => {
          this.connection = null;
          if (error) {
            this.logger('spacetime_disconnect', { message: error.message });
          }
          void this.handleUnexpectedDisconnect();
        });

      try {
        builder.build();
      } catch (error) {
        this.connectPromise = null;
        reject(error as Error);
      }
    })
      .finally(() => {
        this.connectPromise = null;
      });

    return this.connectPromise;
  }

  public async withEphemeralConnection<T>(task: (connection: DbConnection) => Promise<T> | T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const builder = this.builderFactory();
      let settled = false;

      builder
        .withUri(this.config.endpoint)
        .withDatabaseName(this.resolveModuleName());

      if (this.config.walletPrivateKey) {
        builder.withToken(this.config.walletPrivateKey);
      }

      builder
        .onConnect(async (connection) => {
          try {
            this.installDbAccessorAliases(connection);
            await this.subscribeToCoreTables(connection);
            settled = true;
            const result = await task(connection);
            connection.disconnect();
            resolve(result);
          } catch (error) {
            connection.disconnect();
            reject(error as Error);
          }
        })
        .onConnectError((_ctx, error) => {
          if (!settled) {
            settled = true;
            reject(error);
          }
        });

      try {
        builder.build();
      } catch (error) {
        reject(error as Error);
      }
    });
  }

  public requirePlayerId(): string {
    const playerId = this.config.playerId;
    if (!playerId) {
      throw new Error('Missing playerId in SpaceTime config');
    }
    return playerId;
  }

  public startSessionHeartbeat(): void {
    this.stopSessionHeartbeat();
    this.sessionHeartbeatTimer = setInterval(() => {
      void this.refreshSessionLease();
    }, ConnectionManager.SESSION_HEARTBEAT_INTERVAL_MS);
  }

  public stopSessionHeartbeat(): void {
    if (this.sessionHeartbeatTimer !== null) {
      clearInterval(this.sessionHeartbeatTimer);
      this.sessionHeartbeatTimer = null;
    }
  }

  private async sendLogoutSignal(): Promise<void> {
    const connection = this.connection?.isActive ? this.connection : null;
    if (!connection) return;

    try {
      const payload = this.formatReducerArgs(reducers.logout.paramsType, {});
      await connection.callReducerWithParams('logout', reducers.logout.paramsType, payload);
    } catch (error) {
      throw error as Error;
    }
  }

  private async refreshSessionLease(): Promise<void> {
    const connection = this.connection?.isActive ? this.connection : null;
    if (!connection) {
      return;
    }

    try {
      const payload = this.formatReducerArgs(reducers.refreshSessionLease.paramsType, {});
      await connection.callReducerWithParams(
        'refresh_session_lease',
        reducers.refreshSessionLease.paramsType,
        payload
      );
    } catch (error) {
      this.logger('session_heartbeat_failed', {
        message: (error as Error).message
      });
    }
  }

  private async subscribeToCoreTables(connection: DbConnection): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const builder = connection.subscriptionBuilder();

      builder
        .onApplied(() => {
          resolve();
        })
        .onError((ctx) => {
          reject(ctx.event ?? new Error('Core subscription failed'));
        });

      builder.subscribe([
        'SELECT * FROM my_player',
        'SELECT * FROM game_state',
        'SELECT * FROM my_chat_feed'
      ]);
    });
  }

  private async handleUnexpectedDisconnect(): Promise<void> {
    if (this.disconnectExpected || this.disconnectCleanupInFlight) {
      this.disconnectExpected = false;
      this.stopSessionHeartbeat();
      return;
    }

    this.disconnectCleanupInFlight = true;
    this.stopSessionHeartbeat();
    try {
      await this.sendLogoutSignal();
    } catch (error) {
      this.logger('logout_on_disconnect_failed', {
        message: (error as Error).message
      });
    } finally {
      this.disconnectCleanupInFlight = false;
    }
  }

  private formatReducerArgs(paramsType: ProductType, args: Record<string, unknown>): Record<string, unknown> {
    const elements = paramsType.elements ?? [];
    if (elements.length === 1 && elements[0]?.name === 'args') {
      return { args };
    }
    return args;
  }

  private installDbAccessorAliases(connection: DbConnection): void {
    const dbRecord = connection.db as Record<string, unknown>;
    for (const [key, value] of Object.entries(dbRecord)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
      if (!(camelKey in dbRecord)) {
        dbRecord[camelKey] = value;
      }
    }
  }
}
